import { useContext, useRef, useState, useEffect, useCallback } from 'react';
import { CampaignContext } from 'src/contexts';
import { createOrUpdateCampaign } from 'src/services/campaign';
import { ICampaignBudget } from 'src/lib/schemas/campaign/newFlowCampaign';
import { IChannelGroup } from 'src/lib/schemas';
import { generateBudgetBreakdownByPlacements } from 'src/components/campaign/CampaignBudget/parts/utils';

// Global state to coordinate budget updates across hooks
let isBudgetUpdateInProgress = false;
let lastBudgetUpdateTimestamp = 0;
let budgetManagerUpdates = new Set<number>();

export const setBudgetUpdateInProgress = (value: boolean) => {
	isBudgetUpdateInProgress = value;
	if (value) {
		lastBudgetUpdateTimestamp = Date.now();
	}

	// Clean up old entries periodically to prevent memory leaks
	if (budgetManagerUpdates.size > 10) {
		console.log(
			'🧹 Cleaning up old BudgetManager updates, size was:',
			budgetManagerUpdates.size,
		);
		budgetManagerUpdates.clear();
	}
};

export const getBudgetUpdateInProgress = () => {
	return isBudgetUpdateInProgress;
};

export const wasRecentBudgetUpdate = (withinMs: number = 1000) => {
	return Date.now() - lastBudgetUpdateTimestamp < withinMs;
};

export const isUpdateFromBudgetManager = (budgetTotal: number) => {
	const isFromManager = budgetManagerUpdates.has(budgetTotal);
	if (isFromManager) {
		// Remove from set after checking to prevent stale entries
		budgetManagerUpdates.delete(budgetTotal);
		// console.log('🎯 Detected own BudgetManager update:', budgetTotal);
	}
	return isFromManager;
};

interface BudgetUpdateOptions {
	total?: number;
	placements?: string[];
	availableChannelGroups?: IChannelGroup[];
	source: string; // Para logging/debugging
}

interface PendingBudgetUpdate {
	total: number;
	timestamp: number;
	source: string;
	placements?: string[];
	availableChannelGroups?: IChannelGroup[];
}

export const useBudgetManager = () => {
	const { campaign, setCampaign } = useContext(CampaignContext);
	const isUpdatingRef = useRef(false);
	const [pendingUpdatesQueue, setPendingUpdatesQueue] = useState<
		PendingBudgetUpdate[]
	>([]);
	const [currentBudgetRequest, setCurrentBudgetRequest] =
		useState<PendingBudgetUpdate | null>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Process pending updates queue
	const processPendingUpdates = useCallback(async () => {
		if (isUpdatingRef.current || pendingUpdatesQueue.length === 0) {
			return;
		}

		// Get the most recent update (latest user input)
		const latestUpdate = pendingUpdatesQueue[pendingUpdatesQueue.length - 1];
		setCurrentBudgetRequest(latestUpdate);
		setPendingUpdatesQueue([]); // Clear queue since we're processing the latest

		// CRITICAL: Don't process budget updates if campaign doesn't have a valid ID
		// This prevents duplicate API calls when creating a new campaign
		if (!campaign || !campaign.budget || !campaign.id || campaign.id === 'new') {
			console.log('🚫 BudgetManager: Skipping update - campaign not ready', {
				hasCampaign: !!campaign,
				hasBudget: !!campaign?.budget,
				campaignId: campaign?.id,
			});
			setCurrentBudgetRequest(null);
			return;
		}

		isUpdatingRef.current = true;
		setBudgetUpdateInProgress(true);

		try {
			const newTotal = latestUpdate.total;
			const currentPlacements =
				latestUpdate.placements ?? campaign.placements ?? [];
			const channelGroups = latestUpdate.availableChannelGroups ?? [];

			const newBreakdown = generateBudgetBreakdownByPlacements(
				currentPlacements,
				channelGroups,
				newTotal,
			);

			const newBudget: ICampaignBudget = {
				...campaign.budget,
				total: newTotal,
				breakdown: newBreakdown,
			};

			const updatedCampaign = await createOrUpdateCampaign(
				{ budget: newBudget },
				campaign.id,
			);

			// Mark this total as coming from BudgetManager to prevent glitches
			budgetManagerUpdates.add(newTotal);
			// console.log('🏷️ Marked as BudgetManager update:', newTotal, 'Set size:', budgetManagerUpdates.size);

			setCampaign(updatedCampaign);

			return newBudget;
		} catch (error) {
			console.error(
				'❌ BudgetManager: Update failed from',
				latestUpdate.source,
				error,
			);
			return null;
		} finally {
			isUpdatingRef.current = false;
			setBudgetUpdateInProgress(false);
			setCurrentBudgetRequest(null);
		}
	}, [campaign, pendingUpdatesQueue]);

	// Auto-process pending updates with debounce
	// Wait 300ms to batch multiple rapid changes before processing
	useEffect(() => {
		if (pendingUpdatesQueue.length > 0) {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			timeoutRef.current = setTimeout(processPendingUpdates, 300);
		}

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [pendingUpdatesQueue, processPendingUpdates]);

	const queueBudgetUpdate = (
		options: BudgetUpdateOptions,
	): ICampaignBudget | null => {
		const { total, placements, availableChannelGroups, source } = options;

		// Don't queue budget updates if campaign doesn't have a valid ID
		if (!campaign || !campaign.budget || total === undefined || !campaign.id || campaign.id === 'new') {
			console.log('🚫 BudgetManager: Not queuing update - campaign not ready', {
				hasCampaign: !!campaign,
				hasBudget: !!campaign?.budget,
				hasTotal: total !== undefined,
				campaignId: campaign?.id,
				source,
			});
			return null;
		}

		// Create optimistic budget for immediate UI update
		const placementsToUse = placements ?? campaign.placements ?? [];
		const channelGroupsToUse = availableChannelGroups ?? [];

		const newBreakdown = generateBudgetBreakdownByPlacements(
			placementsToUse,
			channelGroupsToUse,
			total,
		);

		const optimisticBudget: ICampaignBudget = {
			...campaign.budget,
			total,
			breakdown: newBreakdown,
		};

		// Add to pending queue
		const pendingUpdate: PendingBudgetUpdate = {
			total,
			timestamp: Date.now(),
			source,
			placements,
			availableChannelGroups,
		};

		setPendingUpdatesQueue((prev) => [...prev, pendingUpdate]);

		return optimisticBudget;
	};

	// Legacy method for backward compatibility
	const updateBudget = async (
		options: BudgetUpdateOptions,
	): Promise<ICampaignBudget | null> => {
		return queueBudgetUpdate(options);
	};

	return {
		updateBudget,
		queueBudgetUpdate,
		isUpdating: isUpdatingRef.current,
		pendingUpdates: pendingUpdatesQueue.length,
		currentRequest: currentBudgetRequest?.source,
	};
};

export default useBudgetManager;
